"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ThemeMode } from "../utils/types";
import { getStorageTheme, setStorageTheme } from "~app/utils/localStorage";

interface ThemeContextProps {
  theme: ThemeMode;
  toggleTheme: (v: ThemeMode) => void;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ThemeContext = createContext<ThemeContextProps>({
  theme: "window98",
  toggleTheme: () => {},
});

const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<ThemeMode>("window98");

  useEffect(() => {
    const localStorageTheme = getStorageTheme();
    setTheme(localStorageTheme || "window98");
  }, []);

  const injectThemeClass = useCallback(
    (newTheme: ThemeMode) => {
      const baseClassname = "theme";
      const htmlElement = document.querySelector("html");
      htmlElement?.classList.remove(`${baseClassname}-${theme}`);
      htmlElement?.classList.add(`${baseClassname}-${newTheme}`);
    },
    [theme]
  );

  const toggleTheme = useCallback(
    (newTheme: ThemeMode) => {
      setTheme(newTheme);
      setStorageTheme(newTheme);
      injectThemeClass(newTheme);
    },
    [injectThemeClass]
  );

  React.useEffect(() => {
    toggleTheme(theme);
  }, [theme, toggleTheme]);

  return (
    <ThemeContext.Provider value={{ theme: theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;
